import { useEffect, useState, useCallback, useMemo } from "react"

import debounce from "lodash/debounce"

import { getCoords } from "@utils/suggestionUtils"
import {
  MAPS_KEY_PRESENT,
  hasGoogleMaps,
  proxyAutocomplete,
} from "@utils/mapsProvider"
import { Prediction } from "types/suggestions"

const useAddressPredictions = (input, detailsRef) => {
  const [predictions, setPredictions] = useState<Prediction[]>([])
  const [placePhotos, setPlacePhotos] = useState([])
  // BUG-003 (2026-07-10): guard the SDK construction -- when the Maps JS key is stripped the SDK
  // never loads, so `new window.google.maps.*` throws and white-screens the whole Where flow.
  // With no SDK, autocomplete + geocode run through the backend provider proxy instead.
  const useSdk = MAPS_KEY_PRESENT && hasGoogleMaps()
  const autocomplete = useMemo(
    () =>
      useSdk ? new window.google.maps.places.AutocompleteService() : null,
    []
  )
  const geocoder = useMemo(
    () => (useSdk ? new window.google.maps.Geocoder() : null),
    []
  )

  const getPlacePredictions = async input => {
    try {
      // Do not bias with `location`/`radius` -- there was never a real
      // coordinate wired in here (the old `new google.maps.LatLng()` with
      // no args produced an invalid NaN,NaN point), which made the Places
      // Autocomplete request come back with zero predictions even though
      // the API call itself returned 200. Plain text search works fine
      // without a location bias.
      const rawPredictions = autocomplete
        ? (await autocomplete.getPlacePredictions({ input }))?.predictions
        : await proxyAutocomplete(input)
      if (!rawPredictions) return
      // Only pre-geocode each prediction when the SDK is available (cheap, in-process). Over the
      // proxy this would be one REST geocode per keystroke per row -- skip it; handleSelect
      // geocodes the chosen place on select, which is all the flow needs.
      if (geocoder) {
        for (const prediction of rawPredictions) {
          const { lat, lng } =
            (await getCoords(geocoder, prediction.place_id)) || {}
          // Skip only this prediction on geocode failure -- a single bad
          // result must not wipe out the whole predictions list (was `return`,
          // which aborted the function before setPredictions ever ran).
          if (!lat || !lng) continue
          prediction.lat = lat
          prediction.lng = lng
        }
      }

      setPredictions(
        rawPredictions.map(prediction => ({
          iconName: "location-marker",
          label: prediction.description,
          ...prediction,
        }))
      )
    } catch (error) {
      console.log(error)
    }
  }

  const debouncedGetPlacePredictions = useCallback(
    debounce(getPlacePredictions, 500),
    []
  )

  useEffect(() => {
    if (!input || input === "Current Location") return
    debouncedGetPlacePredictions(input)
  }, [input])

  // useEffect(() => {
  //   if (!predictions.length) return

  //   const service = new window.google.maps.places.PlacesService(
  //     detailsRef.current
  //   )

  //   predictions.map(prediction => {
  //     const placeId = prediction.place_id

  //     service.getDetails(
  //       {
  //         placeId,
  //         fields: ["photos"],
  //       },
  //       place => {
  //         setPlacePhotos(state => [
  //           ...state,
  //           {
  //             photo: place && place.photos && place.photos[0].getUrl(),
  //             placeId,
  //           },
  //         ])
  //       }
  //     )
  //   })
  // }, [predictions])

  return {
    predictions,
    placePhotos,
  }
}

export default useAddressPredictions
